/** Opção de resposta: texto EN/ES */
export interface QuestionOptionText {
  en: string;
  es: string;
}

/** Dados do formulário de criação de questão (múltipla escolha: 2 a 5 opções) */
export interface CreateQuestionFormData {
  questionText: { en: string; es: string };
  difficultyLevel: string;
  categoryId: string;
  linkedCourseIds: string[];
  options: QuestionOptionText[];
  correctOptionIndex: number;
  explanation: { en: string; es: string };
}

/** Erros de validação por campo (chaves i18n ou mensagem) */
export interface CreateQuestionFormValidationErrors {
  questionText?: string;
  difficultyLevel?: string;
  categoryId?: string;
  options?: string;
  correctOptionIndex?: string;
  optionText?: Record<number, string>;
}

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 5;

function hasAnyText(text: { en: string; es: string }): boolean {
  return (
    (text?.en?.trim() ?? '') !== '' || (text?.es?.trim() ?? '') !== ''
  );
}

/**
 * Valida os dados do formulário de criação de questão.
 * Regras: questionText (pelo menos um idioma), difficulty e category preenchidos,
 * entre 2 e 5 opções com texto cada uma, exatamente um correctOptionIndex válido.
 */
export function validateCreateQuestionForm(
  data: CreateQuestionFormData | null
): {
  valid: boolean;
  errors: CreateQuestionFormValidationErrors;
} {
  const errors: CreateQuestionFormValidationErrors = {};

  if (!data) {
    return { valid: false, errors: { questionText: 'validation_required' } };
  }

  if (!hasAnyText(data.questionText)) {
    errors.questionText = 'validation_question_text_required';
  }

  if (!(data.difficultyLevel?.trim() ?? '')) {
    errors.difficultyLevel = 'validation_difficulty_required';
  }

  if (!(data.categoryId?.trim() ?? '')) {
    errors.categoryId = 'validation_category_required';
  }

  const options = data.options ?? [];
  if (options.length < MIN_OPTIONS) {
    errors.options = 'validation_options_min';
  } else if (options.length > MAX_OPTIONS) {
    errors.options = 'validation_options_max';
  } else {
    const optionErrors: Record<number, string> = {};
    let allOptionsValid = true;
    options.forEach((opt, idx) => {
      if (!hasAnyText(opt)) {
        optionErrors[idx] = 'validation_option_text_required';
        allOptionsValid = false;
      }
    });
    if (!allOptionsValid) {
      errors.optionText = optionErrors;
    }
  }

  const validIndex =
    data.correctOptionIndex >= 0 &&
    data.correctOptionIndex < options.length;
  if (!validIndex && options.length > 0) {
    errors.correctOptionIndex = 'validation_correct_option_required';
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

export { MIN_OPTIONS as CREATE_QUESTION_MIN_OPTIONS, MAX_OPTIONS as CREATE_QUESTION_MAX_OPTIONS };

/** Estado inicial do formulário (2 opções vazias). */
export function getInitialCreateQuestionFormData(): CreateQuestionFormData {
  return {
    questionText: { en: '', es: '' },
    difficultyLevel: '',
    categoryId: '',
    linkedCourseIds: [],
    options: Array.from({ length: MIN_OPTIONS }, () => ({ en: '', es: '' })),
    correctOptionIndex: 0,
    explanation: { en: '', es: '' },
  };
}
